import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Box,
  Divider,
  Chip
} from '@mui/material';
import { Assessment } from '@mui/icons-material';
import { analyticsApi } from '../../services/api';

interface IncomeStatementCategoryData {
  category_name: string;
  color: string;
  monthly_amounts: number[];
  total: number;
}

interface IncomeStatementMonthData {
  month: string;
  total_income: number;
  total_expenses: number;
  net_income: number;
}

interface IncomeStatementData {
  months: string[];
  income_categories: IncomeStatementCategoryData[];
  expense_categories: IncomeStatementCategoryData[];
  monthly_totals: IncomeStatementMonthData[];
}

const IncomeStatementTable: React.FC = () => {
  const [data, setData] = useState<IncomeStatementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await analyticsApi.getIncomeStatement();
        setData(response);
        setError(null);
      } catch (err) {
        setError('Failed to load income statement data');
        console.error('Error fetching income statement:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const renderCategoryRow = (category: IncomeStatementCategoryData, isIncome: boolean = false) => (
    <TableRow key={category.category_name}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            sx={{
              backgroundColor: category.color,
              color: 'white',
              width: 12,
              height: 12,
              minWidth: 12,
              '& .MuiChip-label': {
                padding: 0,
              },
            }}
            label=""
          />
          <Typography variant="body2">
            {category.category_name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{
            color: isIncome ? 'success.main' : 'error.main',
            fontWeight: 'medium',
            fontFamily: 'monospace'
          }}
        >
          {formatCurrency(isIncome ? category.total : -category.total)}
        </Typography>
      </TableCell>
      {category.monthly_amounts.slice().reverse().map((amount, index) => (
        <TableCell key={index} align="right">
          <Typography
            variant="body2"
            sx={{
              color: isIncome ? 'success.main' : 'error.main',
              fontFamily: 'monospace'
            }}
          >
            {formatCurrency(isIncome ? amount : -amount)}
          </Typography>
        </TableCell>
      ))}
    </TableRow>
  );

  const renderTotalRow = (label: string, amounts: number[], isIncome: boolean = false, isNet: boolean = false) => (
    <TableRow sx={{ backgroundColor: isNet ? 'action.hover' : 'background.default' }}>
      <TableCell>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 'bold',
            color: isNet ? 'primary.main' : 'text.primary'
          }}
        >
          {label}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 'bold',
            color: isNet
              ? amounts.reduce((sum, a) => sum + a, 0) >= 0 ? 'success.main' : 'error.main'
              : isIncome ? 'success.main' : 'error.main',
            fontFamily: 'monospace'
          }}
        >
          {formatCurrency(isIncome ? amounts.reduce((sum, amount) => sum + amount, 0) : -amounts.reduce((sum, amount) => sum + amount, 0))}
        </Typography>
      </TableCell>
      {amounts.slice().reverse().map((amount, index) => (
        <TableCell key={index} align="right">
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              color: isNet
                ? amount >= 0 ? 'success.main' : 'error.main'
                : isIncome ? 'success.main' : 'error.main',
              fontFamily: 'monospace'
            }}
          >
            {formatCurrency(isIncome ? amount : -amount)}
          </Typography>
        </TableCell>
      ))}
    </TableRow>
  );

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error">{error}</Alert>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">No data available</Alert>
      </Card>
    );
  }

  return (
    <Card sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Assessment sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Income Statement - Last 5 Months
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: { xs: 650, md: 'auto' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Category</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                Total
              </TableCell>
              {data.months.slice().reverse().map((month, index) => (
                <TableCell key={index} align="right" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                  {month}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Revenue Section */}
            <TableRow>
              <TableCell colSpan={data.months.length + 2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.main', py: 1 }}>
                  REVENUE
                </Typography>
              </TableCell>
            </TableRow>
            
            {data.income_categories.filter(category => category.total > 0).map((category) => renderCategoryRow(category, true))}
            
            {data.income_categories.filter(category => category.total > 0).length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={data.months.length + 2}>
                    <Divider sx={{ my: 1 }} />
                  </TableCell>
                </TableRow>
                {renderTotalRow(
                  'Total Revenue',
                  data.monthly_totals.map(m => m.total_income),
                  true
                )}
              </>
            )}

            {/* Expenses Section */}
            <TableRow>
              <TableCell colSpan={data.months.length + 2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', py: 2 }}>
                  EXPENSES
                </Typography>
              </TableCell>
            </TableRow>

            {data.expense_categories.filter(category => category.total > 0).map((category) => renderCategoryRow(category))}

            {data.expense_categories.filter(category => category.total > 0).length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={data.months.length + 2}>
                    <Divider sx={{ my: 1 }} />
                  </TableCell>
                </TableRow>
                {renderTotalRow(
                  'Total Expenses',
                  data.monthly_totals.map(m => m.total_expenses)
                )}
              </>
            )}

            {/* Net Income */}
            <TableRow>
              <TableCell colSpan={data.months.length + 2}>
                <Divider sx={{ my: 2 }} />
              </TableCell>
            </TableRow>
            {renderTotalRow(
              'NET INCOME',
              data.monthly_totals.map(m => m.net_income),
              false,
              true
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default IncomeStatementTable;